import cn from 'clsx'
import { replace } from 'connected-react-router'
import { rem } from 'polished'
import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { RouteComponentProps, withRouter } from 'react-router'
import styled from 'styled-components'
import moment from 'moment'

import { replaceWithRegion } from 'actions/NavigationActions'
import { fetchPopularHotelFilters, offerListScroll } from 'actions/OfferActions'
import { fetchSpecificPlaces } from 'actions/PlaceActions'
import { getListName } from 'analytics/enhanced-ecommerce'
import {
  searchOfferListFilters,
  searchResultDealClick,
  searchResultImpressionCount,
  searchSessionDealClick,
} from 'analytics/eventDefinitions'
import { impressionEventWithContext, productClickEventWithContext, searchEvent } from 'analytics/snowplow/events'
import { addGTMEvent } from 'api/googleTagManager'
import {
  OfferListEventHandler,
  OfferListEvents,
  OfferListEventsProvider,
} from 'components/OfferList/OfferListEventsContext'
import { SearchFormRef } from 'components/Search'
import { EnabledFilters } from 'components/Search/type'
import { getAppliedFilterCount, getPageTitle } from 'components/Search/utils'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import {
  HOTEL_SEARCH_TYPES,
  OFFER_TYPE_BED_BANK,
  OFFER_TYPE_BUNDLE_AND_SAVE,
  OFFER_TYPE_VILLA,
} from 'constants/offer'
import {
  landmarkSearchSortByTypes,
  searchSortByTypes,
  SORT_OPTION_CUSTOMER_RATING,
  SORT_OPTION_VALUE,
} from 'constants/offerListFilters'
import { OptimizelyExperiments } from 'constants/optimizely'
import { ANYWHERE_NAME, FLEXIBLE_DURATION_RANGE, SEARCH_VERTICALS } from 'constants/search'
import { queryKeyFilters } from 'constants/url'
import { GLOBAL_SEARCH_INITIAL_STATE, GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import { useGlobalHotelsSearchContext } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalHotelsSearch'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { useIsLargeDesktopScreen, useIsMobileScreen } from 'hooks/useScreenSize'
import { getOptimizelyExperimentVariation } from 'lib/optimizely/optimizelyUtils'
import {
  createRecentPropertySearchObject,
  decodeSearchParams,
  getDistanceEqFilter,
  getDistanceLteFilter,
  getSearchKey,
  getSearchTypeFromFilters,
  getsScheduleVisibilitiesFilter,
  isSearchStreamingSupported,
} from 'lib/search/searchUtils'
import {
  deleteSearchParams,
  getCheckInDateFromURLSearchParams,
  getCheckOutDateFromURLSearchParams,
  getFlexibleMonthsFromURLSearchParams,
  getFlexibleNightsFromURLSearchParams,
  parseSearchString,
  setManySearchParamValues,
  setSearchParamValue,
  validateBoundsSearchParams,
  validateDestinationSearchParams,
  validateLandmarkSearchParams,
  validatePropertySearchParams,
} from 'lib/url/searchUrlUtils'
import { isLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { getUrlOfferListFilters } from 'selectors/routerSelectors'

import * as Analytics from 'analytics/analytics'
import BusinessTravellerStickyBudgetBar from 'businessTraveller/components/budget-bar/accommodation/BusinessTravellerStickyBudgetBar'
import ErrorBoundary from 'components/Common/ErrorBoundary'
import HeadData from 'components/Common/HeadData'
import LayoutContainer from 'components/Common/LayoutContainer'
import OfferRecommendations from 'components/Common/OfferRecommendations'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import PromptCardSelector from 'components/Common/StickyPromptCard/PromptCardSelector'
import TextButton from 'components/Luxkit/Button/TextButton'
import LineSearchIcon from 'components/Luxkit/Icons/line/LineSearchIcon'
import Heading from 'components/Luxkit/Typography/Heading'
import OfferList, { OfferListV2Item } from 'components/OfferList/OfferList'
import useIdleTimeReporter, { IdleTimeSource } from 'components/Recommendations/hooks/useIdleTimeReporter'
import useSlimMobileSearch from 'components/SearchV2/Hooks/useSlimMobileSearch'
import config from 'constants/config'
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import GlobalSearchContextProvider from 'contexts/GlobalSearch/GlobalSearchContextProvider'
import ModalContext from 'contexts/ModalContext'
import useGlobalSearchURLHashVertical from 'hooks/GlobalSearch/useGlobalSearchURLHashVertical'
import useSecondChanceModal, { PageType } from 'hooks/Modal/useSecondChanceModal'
import useShouldHighlight from 'hooks/OfferPage/useShouldHighlightTile'
import useOffer from 'hooks/Offers/useOffer'
import useOfferList from 'hooks/Offers/useOfferList'
import useOfferListFilters from 'hooks/Offers/useOfferListFilters'
import useOfferListExtras from 'hooks/Offers/useOfferListExtras'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import useOffersWithProperty from 'hooks/Offers/useOffersWithProperty'
import useStreamSearchExpired from 'hooks/Offers/useStreamSearchExpired'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import useSearchSession from 'hooks/Search/useSearchSession'
import useSpecificPaidLandingPageExperiment from 'hooks/Search/useSpecificPaidLandingPageExperiment'
import useQueryParams from 'hooks/useQueryParams'
import { without } from 'lib/array/arrayUtils'
import isAnyFilterApplied from 'lib/offer/filtersUtils'
import getOfferListKey from 'lib/offer/offerListKey'
import zIndex from 'styles/tools/z-index'
import { SEARCH_STREAMING_PAGE_SIZE } from '../../../../search/initialiseSearchSocket'
import HotelSearchDestinationNoResults from './HotelSearchDestinationNoResults'
import HotelSearchExpandedDestinationOfferList from './HotelSearchExpandedDestinationOfferList'
import HotelSearchHeader from './HotelSearchHeader'
import HotelSearchLowResults from './HotelSearchLowResults'
import HotelSearchNearbyPropertyOfferList from './HotelSearchNearbyPropertyOfferList'
import HotelSearchNoSearchResultSection from './HotelSearchNoSearchResultSection'
import HotelSearchPageFilterPane from './HotelSearchPageFilterPane'
import OfferCountAndSort from './OfferCountAndSort'
import RefreshSearchModal from 'components/SearchV2/Components/Mobile/RefreshSearchModal'
import LoadingAnimationBox from 'components/Common/LoadingAnimationBox/LoadingAnimationBox'
import { addRecentSearch } from 'actions/SearchActions'
import { mapGlobalSearchContextToSnowplowSearchEvent, SearchAnalyticsItemType, SearchAnalyticsLocation } from 'analytics/mapSnowplowSearchTracking'
import useCurrentSiteTakeover from 'hooks/useCurrentSiteTakeover'
import SiteTakeoverSearchBanner from 'components/Common/SiteTakeover/SiteTakeoverSearchBanner'

const destinationTypes = new Set(['destination'])

const ANALYTICS_HOTEL_SEARCH_TAG = 'HS'

const StyledBusinessTravellerStickyBudgetBar = styled(BusinessTravellerStickyBudgetBar)`
  z-index: ${zIndex.stickyHeaderBanner};
`

const DiscoverOtherOffers = styled(VerticalSpacer)`
  text-align: center;
`

const StyledLayoutContainer = styled(LayoutContainer)`
  &.filterPane {
    background-color: ${props => props.theme.palette.neutral.default.eight};
    display: grid;
    padding: ${rem(24)} 0;
    grid-template-columns: minmax(0, 1fr);
    grid-gap: ${rem(32)};

    ${mediaQueryUp.desktop} {
      grid-template: "offer-filters offer-list" auto / ${rem(278)} minmax(0, 2fr);
      padding-top: ${rem(12)};
    }
  }

  &.fullWidth {
    padding: ${rem(16)} 0;
  }
`

const StyledHotelSearchPageFilterPane = styled(HotelSearchPageFilterPane)`
  grid-area: offer-filters;
`

const StyledVerticalSpacer = styled(VerticalSpacer)`
  grid-area: offer-list;
`

function mapState(state: App.State) {
  const filters = getUrlOfferListFilters(state)

  return {
    windowSearch: state.router.location.search,
    filters,
    memberId: state.auth.account.memberId,
    searchResultMetadata: state.offer.searchResultMetadata,
    isFlexibleSearch: !!(filters?.flexibleMonths && filters?.flexibleNights),
    specificPlaces: state.destination.specificPlaces[filters.destinationId ?? ''],
    region: state.geo.currentRegionCode,
  }
}

const connector = connect(mapState)

type PropsFromRedux = ConnectedProps<typeof connector>

interface Props extends PropsFromRedux, RouteComponentProps<any> {}

function HotelSearchPage({
  filters,
  match,
  memberId,
  searchResultMetadata,
  windowSearch,
  isFlexibleSearch,
  location,
  history,
  specificPlaces,
}: Props) {
  const dispatch = useAppDispatch()
  const { toggleSearchVertical } = useGlobalSearchURLHashVertical()
  const isMobileScreen = useIsMobileScreen()
  const isDesktop = useIsLargeDesktopScreen()
  const searchType = getSearchTypeFromFilters(filters)
  const params = useMemo(() => parseSearchString(windowSearch), [windowSearch])
  const searchParams = useMemo(() => decodeSearchParams(windowSearch), [windowSearch])
  const mapViewURL = useMemo(() => `/search/map?${deleteSearchParams(windowSearch, 'sortBy')}`, [windowSearch])
  const isSpoofed = useAppSelector(state => state.auth.account.isSpoofed)
  const showSlimMobile = useSlimMobileSearch()
  const pageSize = SEARCH_STREAMING_PAGE_SIZE

  const searchKey = getSearchKey(params)
  const { searchId } = useSearchSession(searchKey)

  const {
    searchTargetLabel,
    offerDistanceFromSearchTarget,
  } = useMemo(() => ({
    searchTargetLabel: params.landmarkName || params.destinationName || params.propertyName,
    offerDistanceFromSearchTarget: searchResultMetadata.distanceFromSearchTarget[params.landmarkId || params.destinationId],
  }), [
    params.landmarkId,
    params.landmarkName,
    params.destinationId,
    params.destinationName,
    params.propertyName,
    searchResultMetadata.distanceFromSearchTarget,
  ])

  const luxPlusEnabled = useAppSelector(isLuxPlusEnabled)
  const enabledFilters: EnabledFilters = useMemo(() => (
    {
      inclusions: true,
      amenities: true,
      customerRating: true,
      priceFilter: config.ENABLE_HOTEL_SEARCH_PAGE_PRICE_FILTER,
      holidayTypes: searchTargetLabel !== ANYWHERE_NAME,
      locations: searchTargetLabel == ANYWHERE_NAME,
      offerTypes: config.ENABLE_VILLA_RENTALS,
      popularFilters: true,
      luxPlusFeatures: luxPlusEnabled,
    }
  ), [searchTargetLabel, luxPlusEnabled])

  useEffect(() => {
    if (searchParams?.searchItem?.searchType === 'destination') {
      dispatch(fetchSpecificPlaces(searchParams.searchItem.value))
    }
  }, [dispatch, searchParams.searchItem])

  const resetFilters = () => {
    const search = deleteSearchParams(
      windowSearch,
      ...queryKeyFilters,
    )
    dispatch(replace({ search }))
    scrollTo(0, 0)
  }

  const formRef = useRef<SearchFormRef>(null)

  const onBedbankSelectDatesPrompt = useCallback((offerId: string, offerType: App.OfferType, windowSearch: string) => {
    const paramsToSet = [{ paramName: 'offerType', paramValue: offerType }, { paramName: 'offerId', paramValue: offerId }]
    if (isMobileScreen) {
      toggleSearchVertical(SEARCH_VERTICALS.HOTELS, { withDateSelect: true, search: setManySearchParamValues(windowSearch, paramsToSet) })
    } else {
      history.replace({ search: setManySearchParamValues(windowSearch, paramsToSet) })
      formRef.current?.form.current.scrollIntoView()
      formRef.current?.openDatePickerMenu()
    }
  }, [history, isMobileScreen, toggleSearchVertical])
  const queryParams = useQueryParams()
  const { globalHotelsSearchDispatch, globalHotelsSearchState } = useGlobalHotelsSearchContext({
    ...GLOBAL_SEARCH_INITIAL_STATE,
    searchType,
    searchTargetLabel,
    offerDistanceFromSearchTarget,
    onBedbankSelectDatesPrompt,
    eventAnalytics: {
      contextLocation: 'search-list',
      searchId,
    },
  }, queryParams)

  const pageTitle = useMemo(() => {
    const { searchItem } = searchParams
    const checkInDate = getCheckInDateFromURLSearchParams(queryParams)
    const checkOutDate = getCheckOutDateFromURLSearchParams(queryParams)
    const flexibleMonths = getFlexibleMonthsFromURLSearchParams(queryParams)
    const flexibleNights = getFlexibleNightsFromURLSearchParams(queryParams)

    return getPageTitle(searchItem, { checkIn: checkInDate, checkOut: checkOutDate, flexibleMonths, flexibleNights: flexibleNights as FLEXIBLE_DURATION_RANGE })
  }, [queryParams, searchParams])

  const searchedDates = useMemo(() => {
    return {
      checkInDate: getCheckInDateFromURLSearchParams(queryParams)?.format(ISO_DATE_FORMAT),
      checkOutDate: getCheckOutDateFromURLSearchParams(queryParams)?.format(ISO_DATE_FORMAT),
    }
  }, [queryParams])

  useEffect(() => {
    globalHotelsSearchDispatch({
      type: GlobalSearchStateActions.SET_OFFER_DISTANCE_FROM_SEARCH_TARGET,
      offerDistanceFromSearchTarget,
    })
  }, [offerDistanceFromSearchTarget, globalHotelsSearchDispatch])

  useEffect(() => {
    globalHotelsSearchDispatch({
      type: GlobalSearchStateActions.SET_SEARCH_TARGET_LABEL,
      searchTargetLabel,
    })
  }, [searchTargetLabel, globalHotelsSearchDispatch])

  useIdleTimeReporter(IdleTimeSource.HotelSearchPage)

  const invalidSearchParams = useMemo(() =>
    !validateDestinationSearchParams(params) &&
    !validateLandmarkSearchParams(params) &&
    !validatePropertySearchParams(params) &&
    !validateBoundsSearchParams(params),
  [params])

  useEffect(() => {
    const onSearchUrlPath = match.url === location.pathname
    if (invalidSearchParams && onSearchUrlPath) {
      dispatch(replaceWithRegion('/hotels'))
    }
  }, [dispatch, invalidSearchParams, location.pathname, match])

  useEffect(() => {
    dispatch(fetchPopularHotelFilters())
  }, [dispatch])

  // -------- START: LERE AB test for search model --------
  const isLuxuryescapes = config.BRAND === 'luxuryescapes'
  const isSortedByRecommendation = !params.sortBy
  const isDestinationOrLandmark = searchType === 'landmark' || searchType === 'destination'

  const isLereSearchEnabled = useAppSelector(state => getOptimizelyExperimentVariation(state, OptimizelyExperiments.lereSearchModelV2)) === 'on'
  const canUseLereSearch = isLuxuryescapes && isSortedByRecommendation && isDestinationOrLandmark
  // -------- END: LERE AB test for search model --------

  // -------- START: 2nd Chance Modal AB Test --------
  useSecondChanceModal({ pageType: PageType.Search })
  // -------- END: 2nd Chance Modal AB Test --------

  const streamResults = isSearchStreamingSupported(
    {
      ...filters,
      isStream: true,
    },
  )

  const listFilters = useMemo((): App.OfferListFilters => {
    let offerTypes: Array<App.OfferListOfferType> = [...HOTEL_SEARCH_TYPES, OFFER_TYPE_BUNDLE_AND_SAVE, OFFER_TYPE_BED_BANK]
    if (config.ENABLE_VILLA_RENTALS) {
      offerTypes.push(OFFER_TYPE_VILLA)
    }
    if (filters.offerTypes?.includes('rental')) {
      offerTypes = filters.offerTypes
    } else if (filters.offerTypes?.includes('hotel')) {
      offerTypes = without(offerTypes, OFFER_TYPE_VILLA)
    }

    return {
      ...filters,
      offerTypes,
      distanceEq: getDistanceEqFilter(searchType),
      distanceLte: getDistanceLteFilter(searchType),
      includeSpecificDatesResults: true,
      scheduleVisibilities: getsScheduleVisibilitiesFilter(isSpoofed),
      shouldUsePersonalizedSearch: canUseLereSearch && isLereSearchEnabled,
      isLoggedIn: canUseLereSearch && isLereSearchEnabled ? !!memberId : undefined, // used for refresh the list when users logged in
      isStream: streamResults,
      include: ['nearby'],
    }
  }, [filters, searchType, isSpoofed, canUseLereSearch, isLereSearchEnabled, memberId, streamResults])

  const searchContext = useMemo(() => {
    return mapGlobalSearchContextToSnowplowSearchEvent({
      ...globalHotelsSearchState,
      flexibleNights: listFilters.flexibleNights as FLEXIBLE_DURATION_RANGE,
      flexibleMonths: listFilters.flexibleMonths,
      durationRange: listFilters.durationRange as Array<FLEXIBLE_DURATION_RANGE>,
      checkinDate: listFilters.checkIn ? moment(listFilters.checkIn) : undefined,
      checkoutDate: listFilters.checkOut ? moment(listFilters.checkOut) : undefined,
      searchItem: searchParams?.searchItem,
      eventAnalytics: {
        contextLocation: 'search-list' as SearchAnalyticsLocation,
        searchId,
      },
    })
  }, [globalHotelsSearchState, listFilters, searchParams?.searchItem, searchId])

  const offerListFilters = useOfferListFilters(listFilters, { noFetch: true })

  const filterCount = useMemo(() => {
    return getAppliedFilterCount(listFilters, enabledFilters)
  }, [listFilters, enabledFilters])

  const hasFiltersApplied = filterCount > 0

  const offset = useMemo(() => {
    return (parseInt(params.pages) * pageSize) || undefined
  }, [params.pages, pageSize])

  const trackingOptions = useMemo(() => ({
    searchContext,
    contextLocation: 'search-list' as SearchAnalyticsLocation,
    searchItemCategory: 'standard' as SearchAnalyticsItemType,
  }), [searchContext])

  const hotelOfferList = useOfferList(listFilters, {
    noFetch: true,
    streamResults,
    offset,
  }, trackingOptions)

  useEffect(() => {
    if (!streamResults || !params.pages) return

    // Fetch more results when the params.pages number changes
    // This effect triggers an infinite scroll-like behavior for streaming search results
    if (params.pages) {
      dispatch(offerListScroll(listFilters, pageSize))
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamResults, params.pages])

  const searchListKey = hotelOfferList.key

  const shouldTriggerLereSearchTest = !!hotelOfferList.offerCount && (hotelOfferList.offerCount > 0) && canUseLereSearch
  useOptimizelyExperiment(OptimizelyExperiments.lereSearchModelV2, shouldTriggerLereSearchTest)

  const recentSearchOffer = useOffersWithProperty(hotelOfferList.offerIds, searchParams.searchItem?.value)
  useEffect(() => {
    const { searchItem, dates: specificDates, rooms, flexibleSearch } = searchParams

    if (searchItem && (destinationTypes.has(searchType) || searchType === 'property' || searchType === 'landmark')) {
      const dates = {
        checkIn: specificDates?.checkIn,
        checkOut: specificDates?.checkOut,
        flexibleMonths: flexibleSearch?.flexibleMonths,
        flexibleDuration: flexibleSearch?.flexibleNights,
        isAnytime: !(specificDates?.checkIn || specificDates?.checkOut || flexibleSearch?.flexibleMonths || flexibleSearch?.flexibleNights),
      }

      const recentSearchItem = (searchType === 'property' && recentSearchOffer) ?
        createRecentPropertySearchObject(searchItem, recentSearchOffer) :
        searchItem

      dispatch(addRecentSearch(recentSearchItem, dates, rooms))
    }
  }, [dispatch, recentSearchOffer, searchParams, searchType])

  const searchSessionId = useAppSelector(state => state.analytics.search.sessions[searchListKey])

  const listDepth = useRef<number>(0)
  useEffect(() => {
    return () => {
      if (hotelOfferList.offerIds.length) {
        // On a new search, finalise tracking the depth reached on the old search
        addGTMEvent(searchResultImpressionCount(hotelOfferList.offerIds.length, listDepth.current))
        // event sent, now reset the depth we're at. They probably applied a filter and will be at the top again
        listDepth.current = 0
      }
    }
    // eslint-disable-next-line
  }, [searchListKey])

  useEffect(() => {
    if (listFilters) {
      addGTMEvent(searchOfferListFilters(listFilters))
    }
  }, [listFilters])

  const tracking = useOfferListTracking(
    getListName('OfferList', ANALYTICS_HOTEL_SEARCH_TAG, 'Search Page Offer List'),
    {
      ias: {
        list: 'hotels-search',
        listId: searchSessionId,
      },
      snowplow: {
        enabled: false,
      },
      key: getOfferListKey(listFilters),
    },
  )

  const onListEvent = useCallback<OfferListEventHandler>((dispatchAction) => {
    // we only want to fire this event on the main offer list
    // and not the extra offer list to avoid dup events
    if (dispatchAction.type === OfferListEvents.listLoad && searchListKey === dispatchAction.key) {
      Analytics.trackEvent(searchEvent('offer-list-load', 'hotels-search-page', {
        filters,
        searchItem: searchParams.searchItem,
        checkIn: filters.checkIn,
        checkOut: filters.checkOut,
        vertical: SEARCH_VERTICALS.HOTELS,
        occupancies: filters.rooms,
      }))
    }
    if (dispatchAction.type === OfferListEvents.productClick) {
      const { offer, position, leadPrice, duration } = dispatchAction
      addGTMEvent(searchResultDealClick())
      addGTMEvent(searchSessionDealClick(searchSessionId, offer.id))
      Analytics.trackEvent(
        productClickEventWithContext(
          position + 1,
          tracking.listName,
          tracking.listId,
          offer,
          { leadPrice, duration, searchContext },
        ),
      )
    } else if (dispatchAction.type === OfferListEvents.productImpression) {
      const { offer, position, availability, leadPrice, duration } = dispatchAction
      listDepth.current = Math.max(position + 1, listDepth.current)
      Analytics.trackEvent(
        impressionEventWithContext(
          position + 1,
          tracking.listName,
          tracking.listId,
          offer,
          { productAvailable: availability, leadPrice, duration, searchContext },
        ),
      )
    }
  }, [searchSessionId, tracking.listName, tracking.listId, searchContext, searchListKey, filters, searchParams.searchItem])

  const onPageChange = useCallback((page: number) => {
    dispatch(replace({ search: setSearchParamValue(windowSearch, 'pages', page) }))
    // eslint-disable-next-line
  }, [windowSearch])

  const sortOptions = useMemo(() => {
    let options = searchType === 'landmark' ? landmarkSearchSortByTypes : searchSortByTypes

    options = [...options, SORT_OPTION_CUSTOMER_RATING]

    if (config.businessTraveller.currentAccountMode === 'business') {
      options = options.filter((option) => option !== SORT_OPTION_VALUE)
    }

    return options
  }, [searchType])

  const showModal = useContext(ModalContext)
  const streamedExtras = useOfferListExtras(listFilters, { disabled: !streamResults })
  const searchExpired = useStreamSearchExpired(listFilters, { disabled: !streamResults })

  useEffect(() => {
    if (searchExpired) {
      Analytics.trackClientEvent({
        subject: 'expired',
        action: 'show',
        category: 'search',
        type: 'nonInteraction',
      })

      showModal(<RefreshSearchModal/>)
    }
  }, [searchExpired, showModal])

  // Super Slim Tile AB Test
  const firstOfferId = hotelOfferList.offerIds[0]
  const [firstOffer,,] = useOffer(firstOfferId)
  const showHighlight = useShouldHighlight({ offer: firstOffer, filters: listFilters })
  const isSuperSlimOfferTileAbTestEnabled = !!useOptimizelyExperiment(OptimizelyExperiments.superSlimTilesForOfferListPage, showHighlight)
  // Super Slim Tile AB Test

  useSpecificPaidLandingPageExperiment()

  const siteTakeover = useCurrentSiteTakeover()

  if (invalidSearchParams) {
    return null
  }

  const mainListTotal = streamResults ? (hotelOfferList.offerCount ?? 0) : (searchType === 'property' ||
    (!hotelOfferList.offerCount || filters.limit) ?
    hotelOfferList.offerIds.length : hotelOfferList.offerCount)

  const mainListFetching = hotelOfferList.fetching
  const destinationNameForHtml = params.destinationName ? params.destinationName.replace(/\+/g, ' ') : ''

  const positionOfFirstSeparator = streamedExtras.length > 0 ? streamedExtras[0].position : undefined

  const noExactMatchesForPropertySearch = streamResults ? !mainListFetching && positionOfFirstSeparator === 0 :
      !destinationTypes.has(searchType) && mainListTotal === 0 && !mainListFetching

  const showFilterPane = isDesktop && (mainListTotal > 0 || mainListFetching || hasFiltersApplied || (searchType === 'property' && !noExactMatchesForPropertySearch))

  const isZSR = destinationTypes.has(searchType) && mainListTotal === 0 && !mainListFetching

  const offerListFiltersProps = {
    sortOptions,
    searchType,
    switchView: {
      label: 'Map view',
      view: 'map' as const,
      URL: mapViewURL,
    },
    listFilters,
  }

  const isAnytimeSearch = (!filters?.checkIn && !filters?.checkOut) && !isFlexibleSearch
  const sortByPrice = listFilters.sortBy === 'price.asc' || listFilters.sortBy === 'price.desc'
  const sortByValue = listFilters.sortBy === 'value'
  const showLoadingAnimation = mainListFetching && !isAnytimeSearch && streamResults && (sortByPrice || sortByValue)

  const extras: Array<OfferListV2Item> = [{
    id: 'no-results',
    position: 'start',
    element: <HotelSearchNoSearchResultSection searchType={searchType} />,
    show: noExactMatchesForPropertySearch,
  },
  {
    id: 'no-results-dest',
    position: 'start',
    element: <HotelSearchDestinationNoResults location={destinationNameForHtml} filters={listFilters} />,
    show: isZSR,
  },
  {
    id: 'heading',
    position: 'start',
    element: <OfferCountAndSort
      destinationName={params.destinationName}
      specificPlaces={specificPlaces}
      fetching={mainListFetching}
      mainListTotal={mainListTotal}
      sortOptions={sortOptions}
      mapViewUrl={mapViewURL}
      isFlexible={isFlexibleSearch}
      filters={listFilters}
      windowSearch={windowSearch}
      shouldShowFilters={!showFilterPane && !offerListFilters.fetching}
      showHolidayTypes={searchTargetLabel !== ANYWHERE_NAME}
      searchType={searchType}
      enabledFilters={enabledFilters}
      {...(isSuperSlimOfferTileAbTestEnabled && { countTextOverride: 'You were interested in this property' })}
    />,
    show: !isZSR,
  },
  {
    id: 'low-results',
    position: 'start',
    element: <HotelSearchLowResults filters={listFilters} numResults={mainListTotal} />,
    show: mainListTotal > 0 && mainListTotal < 5 && !mainListFetching,
  },
  {
    id: 'destination-expanded-results',
    position: 'end',
    element: <HotelSearchExpandedDestinationOfferList filters={listFilters} enableLere={destinationTypes.has(searchType)} />,
    show: !streamResults && destinationTypes.has(searchType),
  },
  {
    id: 'discover-other-offers',
    position: 'end',
    show: !streamResults && !!(params.destinationName && isAnyFilterApplied(params)) && config.businessTraveller.currentAccountMode !== 'business',
    element: <DiscoverOtherOffers gap={16}>
      <Heading variant="heading4">Discover all offers in</Heading>
      <TextButton
        kind="primary"
        variant="ghost"
        startIcon={<LineSearchIcon />}
        onClick={resetFilters}
        size="large"
      >
        {params.destinationName}
      </TextButton>
    </DiscoverOtherOffers>,
  },
  {
    id: 'nearby-property-offers',
    position: 'end',
    element: <HotelSearchNearbyPropertyOfferList filters={listFilters} />,
    show: !streamResults && searchType === 'property',
  }, {
    id: 'recommendations',
    position: 'end',
    element: <OfferRecommendations useList trackingDescription="Search Results" />,
    show: !streamResults && searchType === 'property' && config.businessTraveller.currentAccountMode !== 'business',
  },
  {
    id: 'loading-animation',
    position: 'start',
    element: <LoadingAnimationBox variant="hotels" />,
    show: showLoadingAnimation,
  },
  ...streamedExtras.map(extra => ({
    id: extra.id,
    position: extra.position,
    element: <Heading variant="heading4">{extra.heading}</Heading>,
    show: streamResults ? 'hasOffers' as const : false,
  })),
  ]

  return (
    <AnalyticsPageContext.Provider value={AnalyticsPage.search}>
      <GlobalSearchContextProvider
        state={globalHotelsSearchState}
        dispatch={globalHotelsSearchDispatch}
      >
        <HeadData noIndex title={pageTitle} />
        {!!siteTakeover && <SiteTakeoverSearchBanner />}
        <HotelSearchHeader
          filters={filters}
          formRef={formRef}
          offerListFiltersProps={offerListFiltersProps}
          searchVertical={SEARCH_VERTICALS.HOTELS}
          baseSearchPath="/search"
          baseSearchMapPath="/search/map"
        />
        <ErrorBoundary fallback={null}>
          <PromptCardSelector tripReengagement />
        </ErrorBoundary>
        {config.businessTraveller.currentAccountMode === 'business' && <StyledBusinessTravellerStickyBudgetBar
          checkInDate={searchedDates.checkInDate}
          checkOutDate={searchedDates.checkOutDate}
        />}
        <StyledLayoutContainer
          size={showFilterPane ? 'xl' : 'lg'}
          className={cn({ filterPane: showFilterPane, fullWidth: showSlimMobile })}
        >
          <StyledVerticalSpacer gap={16}>
            <OfferListEventsProvider tracking={tracking} onListEvent={onListEvent}>
              <OfferList
                filters={listFilters}
                interstitials={extras}
                onPageChange={onPageChange}
                tileStyle="search"
                eagerLoadFirstImage
                defaultPage={params.pages ? parseInt(params.pages) : 1}
                fullWidth={showSlimMobile}
                pageSize={pageSize}
                clientCheckAvailability
                streamResults={streamResults}
                searchId={searchId}
                showLoadingTiles={!showLoadingAnimation}
                trackingOptions={trackingOptions}
              />
            </OfferListEventsProvider>
          </StyledVerticalSpacer>
          {showFilterPane && <StyledHotelSearchPageFilterPane
            filters={listFilters}
            mapViewURL={mapViewURL}
            search={windowSearch}
            enabledFilters={enabledFilters}
            hotelOfferList={hotelOfferList}
          />}
        </StyledLayoutContainer>
      </GlobalSearchContextProvider>
    </AnalyticsPageContext.Provider>
  )
}

export default withRouter(connector(HotelSearchPage))
